import { AccountType, } from '@cardlesshq/constants/src/Products';
import { PaymentMethodOrigin, } from './shared';
var AccountEndpoints;
(function (AccountEndpoints) {
    AccountEndpoints["AddBankAccount"] = "/accounts/add-bank-account";
    AccountEndpoints["AddPendingBankAccount"] = "/accounts/add-pending-bank-account";
    AccountEndpoints["CancelPayment"] = "/accounts/cancel-payment";
    AccountEndpoints["CreateBankAccountViewToken"] = "/accounts/create-bank-account-view-token";
    AccountEndpoints["CreatePresignUrl"] = "/files/presign";
    AccountEndpoints["CreatePresignUrlV2"] = "/files/v2/presign";
    AccountEndpoints["DeleteBankAccount"] = "/accounts/delete-bank-account";
    AccountEndpoints["DeletePendingBankAccount"] = "/accounts/delete-pending-bank-account";
    AccountEndpoints["GetAccountByProcessorAccount"] = "/accounts/get-account-by-processor-account";
    AccountEndpoints["GetAccountSummaries"] = "/accounts/get-account-summaries";
    AccountEndpoints["GetAccountSummariesV2"] = "/accounts/v2/get-account-summaries";
    AccountEndpoints["GetFileStatusPrefix"] = "/files";
    AccountEndpoints["GetFileStatusPrefixV2"] = "/files/v2";
    AccountEndpoints["GetProductFeaturesPrefix"] = "/accounts/products";
    AccountEndpoints["GetProductFeaturesPrefixV2"] = "/accounts/v2/products";
    AccountEndpoints["GetBankAccountInfoByToken"] = "/accounts/get-bank-account-info-by-token";
    AccountEndpoints["GetBankAccounts"] = "/accounts/get-bank-accounts";
    AccountEndpoints["GetBankAccountLinkingAttempts"] = "/accounts/get-bank-account-linking-attempts";
    AccountEndpoints["GetPlaidLinkToken"] = "/accounts/get-plaid-link-token";
    AccountEndpoints["GetScheduledPaymentsByAccount"] = "/accounts/get-scheduled-payments-by-account";
    AccountEndpoints["GetScheduledPaymentsByUser"] = "/accounts/get-scheduled-payments-by-user";
    AccountEndpoints["GetExportedStatementUrl"] = "/accounts/get-exported-statement-url";
    AccountEndpoints["GetStatements"] = "/accounts/get-statements";
    AccountEndpoints["GetStatementUrl"] = "/accounts/get-statement-url";
    AccountEndpoints["GetPrimaryAccount"] = "/accounts/get-primary-account";
    AccountEndpoints["GetUserFromAccount"] = "/accounts/get-user-from-account";
    AccountEndpoints["GetProductChangeOffers"] = "/accounts/get-product-change-offers";
    AccountEndpoints["GetProductChangeHistory"] = "/accounts/get-product-change-history";
    AccountEndpoints["GetProductChangePreview"] = "/accounts/get-product-change-preview";
    AccountEndpoints["InitiateProductChange"] = "/accounts/initiate-product-change";
    AccountEndpoints["GetProductChangeRevertPreview"] = "/accounts/get-product-change-revert-preview";
    AccountEndpoints["RevertProductChange"] = "/accounts/revert-product-change";
    AccountEndpoints["AdminSendProductChangeDowngradeEmail"] = "/accounts/admin/send-product-change-downgrade-email";
    AccountEndpoints["InitiateAccountTransfer"] = "/accounts/v2/initiate-account-transfer";
    AccountEndpoints["SchedulePayment"] = "/accounts/schedule-payment";
    AccountEndpoints["UpdateScheduledPayment"] = "/accounts/update-scheduled-payment";
})(AccountEndpoints || (AccountEndpoints = {}));
/*
 * Account can active, frozen, or closed.
 * These statuses need to be updated with our processor as well as within our own system.
 */
var AccountStatus;
(function (AccountStatus) {
    AccountStatus["Unspecified"] = "ACCOUNT_STATUS_UNSPECIFIED";
    /** Account is active and in good standing */
    AccountStatus["Active"] = "ACTIVE";
    /**
     * Account activity is frozen
     * This could be for a variety of reasons, including suspected fraudulent activity.
     */
    AccountStatus["Frozen"] = "FROZEN";
    /** Account has been permanently closed */
    AccountStatus["Closed"] = "CLOSED";
    /** Account has missed payment(s) */
    AccountStatus["Delinquent"] = "DELINQUENT";
    /**
     * Account has an amount of debt is unlikely to be collected.
     * A charge-off is a form of write-off.
     * Traditionally, creditors make this declaration at the point of six months without payment.
     * Never set on the account item — comes dynamically from CoreCard.
     */
    AccountStatus["ChargedOff"] = "CHARGED_OFF";
    /** Account is frozen for possibility of first party fraud */
    AccountStatus["SuspectedFirstPartyFraud"] = "SUSPECTED_FIRST_PARTY_FRAUD";
    /** Account is frozen for possibility of third party fraud */
    AccountStatus["SuspectedThirdPartyFraud"] = "SUSPECTED_THIRD_PARTY_FRAUD";
    /** Account is closed for confirmed first party fraud — the customer is responsible for repayment */
    AccountStatus["ClosedFirstPartyFraud"] = "CLOSED_FIRST_PARTY_FRAUD";
    /** Account is closed for confirmed third party fraud — the customer is not responsible for repayment */
    AccountStatus["ClosedThirdPartyFraud"] = "CLOSED_THIRD_PARTY_FRAUD";
    /**
     * Ops will update this when the account is placed into Collections via Admin Panel
     * CC will map the collections status to the “closed” generated status
     */
    AccountStatus["Collections"] = "COLLECTIONS";
    /**
     * Account is closed for confirmed bankruptcy, set in Admin Panel
     */
    AccountStatus["Bankruptcy"] = "BANKRUPTCY";
    /**
     * Cardholder is deceased, set in Admin Panel
     */
    AccountStatus["Deceased"] = "DECEASED";
    /** Account is flagged as OFAC suspicious */
    AccountStatus["OfacSuspicious"] = "OFAC_SUSPICIOUS";
    /** Account closed due to ATO (account takeover) */
    AccountStatus["ClosedAto"] = "CLOSED_ATO";
    /** Account is ready for payoff (for secured deposit) */
    AccountStatus["ClosedPayoff"] = "CLOSED_PAYOFF";
})(AccountStatus || (AccountStatus = {}));
/**
 * Why an account is in its current status. Paired with a status-log
 * entry or {@link AccountStatusReason}
 */
var AccountStatusReasonCode;
(function (AccountStatusReasonCode) {
    AccountStatusReasonCode["Unspecified"] = "ACCOUNT_STATUS_REASON_CODE_UNSPECIFIED";
    AccountStatusReasonCode["PaymentPastDue"] = "PAYMENT_PAST_DUE";
    AccountStatusReasonCode["SuspectedFraud"] = "SUSPECTED_FRAUD";
    AccountStatusReasonCode["UserRequested"] = "USER_REQUESTED";
    AccountStatusReasonCode["BustOut"] = "BUST_OUT";
    AccountStatusReasonCode["ChargeBack"] = "CHARGE_BACK";
    AccountStatusReasonCode["ReturnPayment"] = "RETURN_PAYMENT";
    AccountStatusReasonCode["PendingCustomerContact"] = "PENDING_CUSTOMER_CONTACT";
    AccountStatusReasonCode["Abuse"] = "ABUSE";
    AccountStatusReasonCode["AccountTakeover"] = "ACCOUNT_TAKEOVER";
    AccountStatusReasonCode["IdentityTheft"] = "IDENTITY_THEFT";
    AccountStatusReasonCode["SyntheticIdentityCreation"] = "SYNTHETIC_IDENTITY_CREATION";
    AccountStatusReasonCode["ViolationOfTermsAndConditions"] = "VIOLATION_OF_TERMS_AND_CONDITIONS";
    AccountStatusReasonCode["Inactivity"] = "INACTIVITY";
    AccountStatusReasonCode["TwoCyclesDelinquent"] = "TWO_CYCLES_DELINQUENT";
    AccountStatusReasonCode["CreditorsRequest"] = "CREDITORS_REQUEST";
    AccountStatusReasonCode["CollectionsPlacement"] = "COLLECTIONS_PLACEMENT";
    AccountStatusReasonCode["BankruptcyFiling"] = "BANKRUPTCY_FILING";
    AccountStatusReasonCode["Deceased"] = "DECEASED";
    AccountStatusReasonCode["ProgramDeprecation"] = "PROGRAM_DEPRECATION";
    AccountStatusReasonCode["PartnerInitiated"] = "PARTNER_INITIATED";
})(AccountStatusReasonCode || (AccountStatusReasonCode = {}));
/** Origin of an account-status change, recorded on each status-log entry */
var AccountChangeSource;
(function (AccountChangeSource) {
    AccountChangeSource["Unspecified"] = "ACCOUNT_CHANGE_SOURCE_UNSPECIFIED";
    AccountChangeSource["Automated"] = "AUTOMATED";
    AccountChangeSource["CustomerSupport"] = "CUSTOMER_SUPPORT";
    AccountChangeSource["Partner"] = "PARTNER";
    AccountChangeSource["CollectionsAutomation"] = "COLLECTIONS_AUTOMATION";
})(AccountChangeSource || (AccountChangeSource = {}));
/** Role of an authorized user on an account */
var AuthorizedUserAccountType;
(function (AuthorizedUserAccountType) {
    AuthorizedUserAccountType["Unspecified"] = "AUTHORIZED_USER_ACCOUNT_TYPE_UNSPECIFIED";
    AuthorizedUserAccountType["AccountManager"] = "ACCOUNT_MANAGER";
    AuthorizedUserAccountType["AuthorizedUser"] = "AUTHORIZED_USER";
    AuthorizedUserAccountType["Cardholder"] = "CARDHOLDER";
})(AuthorizedUserAccountType || (AuthorizedUserAccountType = {}));
var PaymentAmountOption;
(function (PaymentAmountOption) {
    PaymentAmountOption["Unspecified"] = "PAYMENT_AMOUNT_OPTION_UNSPECIFIED";
    /**
     * The amount that must be paid by the due date to avoid becoming late.
     * Paying this amount will ensure that no late fees are charged.
     */
    PaymentAmountOption["MinimumPaymentDue"] = "MINIMUM_PAYMENT_DUE";
    /**
     * The new balance on the statement. It doesn't reflect any payments or
     * new transactions since the statement close date.
     */
    PaymentAmountOption["StatementBalance"] = "STATEMENT_BALANCE";
    /** The current balance on the account, including all new payments and transactions */
    PaymentAmountOption["CurrentBalance"] = "CURRENT_BALANCE";
    /** Custom, fixed amount */
    PaymentAmountOption["FixedAmount"] = "FIXED_AMOUNT";
})(PaymentAmountOption || (PaymentAmountOption = {}));
var PaymentFrequency;
(function (PaymentFrequency) {
    PaymentFrequency["Unspecified"] = "PAYMENT_FREQUENCY_UNSPECIFIED";
    /**Payment will be made once */
    PaymentFrequency["OneTime"] = "ONE_TIME";
    /** Recurring payment, will always happen on the due date for the account */
    PaymentFrequency["AutomaticPaymentDueDate"] = "AUTOMATIC_PAYMENT_DUE_DATE";
})(PaymentFrequency || (PaymentFrequency = {}));
var ScheduleStatus;
(function (ScheduleStatus) {
    ScheduleStatus["Unspecified"] = "SCHEDULE_STATUS_UNSPECIFIED";
    /**
     * Payment schedule has been created, but payment has not yet initiated.
     * Payment can still be updated/cancelled.
     */
    ScheduleStatus["Active"] = "ACTIVE";
    /** Payment from this schedule failed */
    ScheduleStatus["Failed"] = "FAILED";
    /**
     * Payment from this schedule has been completed.
     * Only applicable for one-time payment schedules.
     * For recurring payments, the schedule status will remain "ACTIVE" after each payment has been completed.
     * Once a payment schedule is "COMPLETED", the payment from this schedule should appear in the transactions list.
     */
    ScheduleStatus["Completed"] = "COMPLETED";
    /** Payment has been cancelled by the user */
    ScheduleStatus["Cancelled"] = "CANCELLED";
    /** Payment has been initiated, but not yet completed */
    ScheduleStatus["Initiated"] = "INITIATED";
    /** Payment has been skipped- should be considered a completed state */
    ScheduleStatus["Skipped"] = "SKIPPED";
    /** Payment has been reversed after completion */
    ScheduleStatus["Reversed"] = "REVERSED";
    /** Payment has been submitted to the bank, but not yet to the FED. Currently only for Column initiated payments */
    ScheduleStatus["Submitted"] = "SUBMITTED";
})(ScheduleStatus || (ScheduleStatus = {}));
var SpendRestrictionType;
(function (SpendRestrictionType) {
    SpendRestrictionType["Unspecified"] = "SPEND_RESTRICTION_TYPE_UNSPECIFIED";
    /** Spend is capped between account approval and the primary cardholder activating their physical card */
    SpendRestrictionType["PreActivationCap"] = "PRE_ACTIVATION_CAP";
})(SpendRestrictionType || (SpendRestrictionType = {}));
var BankAccountType;
(function (BankAccountType) {
    BankAccountType["Unspecified"] = "BANK_ACCOUNT_TYPE_UNSPECIFIED";
    BankAccountType["Checking"] = "CHECKING";
    BankAccountType["Savings"] = "SAVINGS";
})(BankAccountType || (BankAccountType = {}));
/** Outcome of a single bank-account linking attempt. */
var BankAccountLinkingStatus;
(function (BankAccountLinkingStatus) {
    BankAccountLinkingStatus["Unspecified"] = "BANK_ACCOUNT_LINKING_STATUS_UNSPECIFIED";
    BankAccountLinkingStatus["Success"] = "BANK_ACCOUNT_LINKING_STATUS_SUCCESS";
    BankAccountLinkingStatus["Failure"] = "BANK_ACCOUNT_LINKING_STATUS_FAILURE";
})(BankAccountLinkingStatus || (BankAccountLinkingStatus = {}));
/** Why a linking attempt failed. Only set on {@link BankAccountLinkingStatus.Failure} attempts. */
var BankAccountLinkingErrorType;
(function (BankAccountLinkingErrorType) {
    BankAccountLinkingErrorType["Unspecified"] = "BANK_ACCOUNT_LINKING_ERROR_TYPE_UNSPECIFIED";
    BankAccountLinkingErrorType["PlaidError"] = "PLAID_ERROR";
    BankAccountLinkingErrorType["ValidationError"] = "VALIDATION_ERROR";
    BankAccountLinkingErrorType["ProcessorError"] = "PROCESSOR_ERROR";
    BankAccountLinkingErrorType["DynamoDbError"] = "DYNAMODB_ERROR";
    BankAccountLinkingErrorType["UnauthorizedError"] = "UNAUTHORIZED_ERROR";
    BankAccountLinkingErrorType["DuplicateAccountError"] = "DUPLICATE_ACCOUNT_ERROR";
    BankAccountLinkingErrorType["ItemNotFoundError"] = "ITEM_NOT_FOUND_ERROR";
    BankAccountLinkingErrorType["UnknownError"] = "UNKNOWN_ERROR";
})(BankAccountLinkingErrorType || (BankAccountLinkingErrorType = {}));
var PendingBankAccountStatus;
(function (PendingBankAccountStatus) {
    PendingBankAccountStatus["Unspecified"] = "PENDING_BANK_ACCOUNT_STATUS_UNSPECIFIED";
    /** Bank account will be verified or rejected without any additional user input */
    PendingBankAccountStatus["PendingAutomaticVerification"] = "PENDING_AUTOMATIC_VERIFICATION";
    /**
     * Verification requires user input.
     * e.g. verifying microdeposits
     */
    PendingBankAccountStatus["PendingManualVerification"] = "PENDING_MANUAL_VERIFICATION";
    /**
     * Verification has expired.
     * User will need to attempt the verification again.
     */
    PendingBankAccountStatus["VerificationExpired"] = "VERIFICATION_EXPIRED";
})(PendingBankAccountStatus || (PendingBankAccountStatus = {}));
var IdentityVerificationVendor;
(function (IdentityVerificationVendor) {
    IdentityVerificationVendor["VendorUnspecified"] = "VENDOR_UNSPECIFIED";
    /** Plaid Identity Verification handled via Plaid API/webhooks */
    IdentityVerificationVendor["PlaidV1"] = "PLAID";
    /** Plaid IDV handled by Effectiv workflow/webhooks */
    IdentityVerificationVendor["PlaidV2"] = "PLAID_V2";
    /** Socure Document Verification handled by Effectiv workflow/webhooks */
    IdentityVerificationVendor["Socure"] = "SOCURE";
})(IdentityVerificationVendor || (IdentityVerificationVendor = {}));
var ExportTransactionsRequestStatus;
(function (ExportTransactionsRequestStatus) {
    ExportTransactionsRequestStatus["EXPORT_TRANSACTIONS_REQUEST_STATUS_UNSPECIFIED"] = "EXPORT_TRANSACTIONS_REQUEST_STATUS_UNSPECIFIED";
    ExportTransactionsRequestStatus["PENDING"] = "PENDING";
    ExportTransactionsRequestStatus["SUCCESS"] = "SUCCESS";
    ExportTransactionsRequestStatus["ERROR"] = "ERROR";
    ExportTransactionsRequestStatus["NOT_SENT"] = "NOT_SENT";
})(ExportTransactionsRequestStatus || (ExportTransactionsRequestStatus = {}));
/** Payment processor backing an account */
var ProcessorName;
(function (ProcessorName) {
    ProcessorName["Unspecified"] = "PROCESSOR_NAME_UNSPECIFIED";
    ProcessorName["Corecard"] = "CORECARD";
})(ProcessorName || (ProcessorName = {}));
var AccountTransferDirection;
(function (AccountTransferDirection) {
    AccountTransferDirection["Credit"] = "ACCOUNT_TRANSFER_DIRECTION_CREDIT";
})(AccountTransferDirection || (AccountTransferDirection = {}));
var AccountTransferStatus;
(function (AccountTransferStatus) {
    AccountTransferStatus["Pending"] = "ACCOUNT_TRANSFER_STATUS_PENDING";
})(AccountTransferStatus || (AccountTransferStatus = {}));
var BankTransferMovement;
(function (BankTransferMovement) {
    BankTransferMovement["Ach"] = "BANK_TRANSFER_MOVEMENT_ACH";
})(BankTransferMovement || (BankTransferMovement = {}));
var RewardsAccountStatus;
(function (RewardsAccountStatus) {
    RewardsAccountStatus["Unspecified"] = "REWARDS_LINKING_STATUS_UNSPECIFIED";
    RewardsAccountStatus["Linked"] = "REWARDS_LINKING_STATUS_LINKED";
    RewardsAccountStatus["RelinkRequired"] = "REWARDS_LINKING_STATUS_RELINK_REQUIRED";
})(RewardsAccountStatus || (RewardsAccountStatus = {}));
var PaymentMethodType;
(function (PaymentMethodType) {
    PaymentMethodType["Unspecified"] = "PAYMENT_METHOD_TYPE_UNSPECIFIED";
    PaymentMethodType["BankAccount"] = "BANK_ACCOUNT";
    PaymentMethodType["PartnerAccount"] = "PARTNER_ACCOUNT";
    PaymentMethodType["ExternalBill"] = "EXTERNAL_BILL";
    PaymentMethodType["Check"] = "CHECK";
    PaymentMethodType["LockBox"] = "LOCK_BOX";
})(PaymentMethodType || (PaymentMethodType = {}));
/**
 * Origin of the payment schedule at time of creation.
 */
var PaymentOrigin;
(function (PaymentOrigin) {
    PaymentOrigin["Unspecified"] = "PAYMENT_METHOD_ORIGIN_UNSPECIFIED";
    PaymentOrigin["Cardless"] = "CARDLESS";
    PaymentOrigin["Partner"] = "PARTNER";
    PaymentOrigin["Other"] = "OTHER";
})(PaymentOrigin || (PaymentOrigin = {}));
var ServicingType;
(function (ServicingType) {
    ServicingType["ServicingTypeUnspecified"] = "SERVICING_TYPE_UNSPECIFIED";
    /** Main usage is through the Cardless app */
    ServicingType["CardlessApp"] = "CARDLESS_APP";
    /** Main usage is through a partner app via an API */
    ServicingType["PartnerAppApi"] = "PARTNER_APP_API";
    /** Main usage is through a partner app via a webview */
    ServicingType["PartnerAppWebview"] = "PARTNER_APP_WEBVIEW";
})(ServicingType || (ServicingType = {}));
var FileStatus;
(function (FileStatus) {
    FileStatus["FileStatusUnspecified"] = "FILE_STATUS_UNSPECIFIED";
    FileStatus["Pending"] = "PENDING";
    FileStatus["Uploaded"] = "UPLOADED";
    FileStatus["Expired"] = "EXPIRED";
})(FileStatus || (FileStatus = {}));
var FileType;
(function (FileType) {
    FileType["FileTypeUnspecified"] = "FILE_TYPE_UNSPECIFIED";
    FileType["DisputeSupportingDocument"] = "DISPUTE_SUPPORTING_DOCUMENT";
})(FileType || (FileType = {}));
/**
 * Internal proto statuses. Only Unspecified carries the PRODUCT_CHANGE_STATUS_ prefix; the proto
 * leaves the rest unprefixed so the JSON matches the product_change.* webhook status.
 */
var ProductChangeProtoStatus;
(function (ProductChangeProtoStatus) {
    ProductChangeProtoStatus["Unspecified"] = "PRODUCT_CHANGE_STATUS_UNSPECIFIED";
    /** Change recorded and the workflow started; the snapshot may not be populated yet. */
    ProductChangeProtoStatus["Initiated"] = "INITIATED";
    /** Tier moved and cards reconciled; fee assessment still pending. */
    ProductChangeProtoStatus["Transferred"] = "TRANSFERRED";
    ProductChangeProtoStatus["Completed"] = "COMPLETED";
    /** Reversal in flight, from Transferred or Completed; transient, precedes Reverted. */
    ProductChangeProtoStatus["RevertInitiated"] = "REVERT_INITIATED";
    ProductChangeProtoStatus["Reverted"] = "REVERTED";
})(ProductChangeProtoStatus || (ProductChangeProtoStatus = {}));
/** Externally exposed API statuses. */
var ProductChangeStatus;
(function (ProductChangeStatus) {
    ProductChangeStatus["Initiated"] = "INITIATED";
    ProductChangeStatus["Transferred"] = "TRANSFERRED";
    ProductChangeStatus["Completed"] = "COMPLETED";
    ProductChangeStatus["RevertInitiated"] = "REVERT_INITIATED";
    ProductChangeStatus["Reverted"] = "REVERTED";
})(ProductChangeStatus || (ProductChangeStatus = {}));
/** Whether a cardholder was on the account at the upgrade being reverted. */
var ProductChangeRevertMembership;
(function (ProductChangeRevertMembership) {
    ProductChangeRevertMembership["Unspecified"] = "MEMBERSHIP_UNSPECIFIED";
    /** Present at upgrade; included in the reversal. */
    ProductChangeRevertMembership["ActiveAtUpgrade"] = "ACTIVE_AT_UPGRADE";
    /** Added after the upgrade; excluded, and settled under downgrade logic instead. */
    ProductChangeRevertMembership["AddedAfterUpgrade"] = "ADDED_AFTER_UPGRADE";
})(ProductChangeRevertMembership || (ProductChangeRevertMembership = {}));
export { AccountChangeSource, AccountEndpoints, AccountStatus, AccountStatusReasonCode, AccountType, AuthorizedUserAccountType, BankAccountLinkingErrorType, BankAccountLinkingStatus, BankAccountType, ExportTransactionsRequestStatus, FileStatus, FileType, IdentityVerificationVendor, PaymentAmountOption, PaymentFrequency, PaymentMethodOrigin, PaymentMethodType, PaymentOrigin, PendingBankAccountStatus, ProcessorName, RewardsAccountStatus, ScheduleStatus, ServicingType, SpendRestrictionType, ProductChangeProtoStatus, ProductChangeStatus, ProductChangeRevertMembership, AccountTransferDirection, AccountTransferStatus, BankTransferMovement, };
